import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import {
    Undo2, Upload, Loader2, CheckCircle, AlertTriangle, Camera,
    Sparkles, ArrowDownToLine, Receipt, X, FileText, History, Plus, Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ReceiptUploadModal } from '../../Financials/components/ReceiptUploadModal';
import { CreditNoteUploadModal } from './CreditNoteUploadModal';
import { reportError } from '../../../lib/errors';

type ReturnStatus = 'pending_collection' | 'collected' | 'credit_matched' | 'credited' | 'rejected' | 'write_off' | 'reclaimed';

interface PartReturn {
    id: string;
    org_id: string;
    job_id: string;
    vrm: string;
    part_name: string;
    supplier: string;
    cost_price: number;
    retail_price: number;
    quantity: number;
    vat_amount: number;
    return_reason: string;
    return_type: string;
    status: ReturnStatus;
    notes: string | null;
    collection_due_date: string | null;
    collected_at: string | null;
    credited_at: string | null;
    credit_amount: number | null;
    created_at: string;
}

const PENDING_STATUSES: ReturnStatus[] = ['pending_collection', 'collected', 'credit_matched'];

type CreditGranularity = 'week' | 'month' | 'all';

interface SupplierCreditRow {
    supplier: string;
    period_start: string | null;
    total_credit: number;
    credit_count: number;
    is_current: boolean;
}

function formatPeriodLabel(granularity: CreditGranularity, periodStart: string | null): string {
    if (granularity === 'all' || !periodStart) return 'All time';
    const d = new Date(periodStart + 'T00:00:00');
    if (granularity === 'month') {
        return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
    return 'Week of ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysSince(dateStr: string): number {
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

function agingColor(days: number): { text: string; bg: string } {
    if (days < 7) return { text: 'text-success', bg: 'bg-success/10 border-success/20' };
    if (days < 21) return { text: 'text-gold', bg: 'bg-gold-fill/10 border-gold/20' };
    return { text: 'text-danger', bg: 'bg-danger/10 border-danger/20' };
}

// ESTIMATE ONLY. Assumes a basic-rate sole trader: 20% income tax + 9% Class 4
// NIC = 29% marginal relief on a deductible write-off. This is WRONG for
// higher-rate traders (~42%) and is shown purely as a rough "~£X" guide.
// The real saving is whatever the write-off's ledger entry reduces taxable
// profit by — see the Safe-to-Spend engine. TODO: derive the marginal rate
// from the org's projected tax band instead of this constant.
// 20% basic-rate income tax + 6% Class 4 NIC.
//
// Was 0.29, carrying the pre-2024 Class 4 rate of 9%, which overstated every
// saving shown on this screen by three points. The rest of the app already
// uses 6% — see nicBandLabel in lib/taxBands.ts ("6% on profit over £12,570")
// and the 2026/27 model the marketing calculator mirrors. In a product that
// sells getting the tax right, an inflated saving is worse than a modest one.
const TAX_SAVING_RATE = 0.26;
const TAX_SAVING_ESTIMATE_TOOLTIP =
    'Rough estimate only: 20% basic-rate income tax + 6% Class 4 NIC = 26%. ' +
    'Higher-rate traders save more. The actual saving is set by your ledger, not this figure.';

export default function ReturnsBayPage() {
    const [returns, setReturns] = useState<PartReturn[]>([]);
    const [loading, setLoading] = useState(true);
    const [orgId, setOrgId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'pending' | 'writeoffs' | 'history'>('pending');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [creditNoteUploadOpen, setCreditNoteUploadOpen] = useState(false);
    const [creditPromptId, setCreditPromptId] = useState<string | null>(null);
    const [creditAmountInput, setCreditAmountInput] = useState<string>('');
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    // Supplier-credit history state
    const [granularity, setGranularity] = useState<CreditGranularity>('month');
    const [summaryRows, setSummaryRows] = useState<SupplierCreditRow[]>([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [addSupplier, setAddSupplier] = useState('');
    const [addAmount, setAddAmount] = useState('');
    const [addNote, setAddNote] = useState('');
    const [addingCredit, setAddingCredit] = useState(false);
    const [addFormOpen, setAddFormOpen] = useState(false);

    const fetchReturns = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('active_org_id')
            .eq('id', user.id)
            .single();
        if (profileError) reportError(profileError, 'returns-bay-page.profiles');
        if (!profile?.active_org_id) return;
        setOrgId(profile.active_org_id);
        const { data, error: fetchError } = await supabase
            .from('part_returns')
            .select('*')
            .eq('org_id', profile.active_org_id)
            .order('created_at', { ascending: false });
        if (fetchError) reportError(fetchError, 'returns-bay-page.part-returns');
        if (data) setReturns(data as PartReturn[]);
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchReturns().finally(() => setLoading(false));
    }, [fetchReturns]);

    const pending = useMemo(
        () => returns.filter(r => PENDING_STATUSES.includes(r.status)),
        [returns]
    );

    const writeOffs = useMemo(
        () => returns.filter(r => r.status === 'write_off'),
        [returns]
    );

    const pendingTotal = useMemo(
        () => pending.reduce((sum, r) => sum + r.cost_price * r.quantity, 0),
        [pending]
    );

    const pendingSupplierCount = useMemo(() => {
        const set = new Set(pending.map(r => r.supplier));
        return set.size;
    }, [pending]);

    const writeOffsThisMonth = useMemo(() => {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return writeOffs.filter(r => new Date(r.created_at) >= monthStart);
    }, [writeOffs]);

    const writeOffTotalMonth = useMemo(
        () => writeOffsThisMonth.reduce((sum, r) => sum + r.cost_price * r.quantity, 0),
        [writeOffsThisMonth]
    );

    const handleMarkCredited = async (returnId: string) => {
        if (!orgId) return;
        const amount = parseFloat(creditAmountInput);
        if (isNaN(amount) || amount <= 0) return;
        setUpdatingId(returnId);
        try {
            const { error } = await supabase.rpc('fn_update_return_status', {
                p_org_id: orgId,
                p_return_id: returnId,
                p_new_status: 'credited',
                p_credit_amount: amount,
            });
            if (error) throw error;
            const credited = returns.find((r) => r.id === returnId);
            toast.success(
                credited
                    ? `£${amount.toFixed(2)} credit from ${credited.supplier} recorded — added to your P&L and tax pots.`
                    : `£${amount.toFixed(2)} credit recorded — added to your P&L and tax pots.`,
                { duration: 4000 }
            );
            await fetchReturns();
            setCreditPromptId(null);
            setCreditAmountInput('');
        } catch (err) {
            console.error('Failed to mark as credited:', err);
            toast.error(err instanceof Error ? err.message : 'Could not record credit');
        } finally {
            setUpdatingId(null);
        }
    };

    const startCreditPrompt = (r: PartReturn) => {
        setCreditPromptId(r.id);
        setCreditAmountInput((r.cost_price * r.quantity).toFixed(2));
    };

    const fetchSummary = useCallback(async (orgToUse: string, gran: CreditGranularity) => {
        setSummaryLoading(true);
        try {
            const { data, error } = await supabase.rpc('fn_get_supplier_credit_summary', {
                p_org_id: orgToUse,
                p_granularity: gran,
            });
            if (error) throw error;
            setSummaryRows((data as SupplierCreditRow[]) ?? []);
        } catch (err) {
            console.error('Failed to load supplier credit summary:', err);
            setSummaryRows([]);
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'history' && orgId) {
            fetchSummary(orgId, granularity);
        }
    }, [activeTab, granularity, orgId, fetchSummary]);

    const handleAddManualCredit = async () => {
        if (!orgId) return;
        const amount = parseFloat(addAmount);
        if (!addSupplier.trim() || isNaN(amount) || amount <= 0) return;
        setAddingCredit(true);
        try {
            const { error } = await supabase.rpc('fn_record_manual_supplier_credit', {
                p_org_id: orgId,
                p_supplier: addSupplier.trim(),
                p_gross: amount,
                p_note: addNote.trim() || null,
            });
            if (error) throw error;
            setAddSupplier('');
            setAddAmount('');
            setAddNote('');
            setAddFormOpen(false);
            await fetchSummary(orgId, granularity);
        } catch (err) {
            console.error('Failed to record manual supplier credit:', err);
        } finally {
            setAddingCredit(false);
        }
    };

    // Known supplier names for the manual-entry datalist (from returns seen so far).
    const knownSuppliers = useMemo(
        () => Array.from(new Set(returns.map(r => r.supplier).filter(Boolean))).sort(),
        [returns]
    );

    // Group summary rows by period, preserving the RPC's DESC ordering.
    const periodGroups = useMemo(() => {
        const groups: { key: string; label: string; isCurrent: boolean; rows: SupplierCreditRow[]; total: number }[] = [];
        const byKey = new Map<string, typeof groups[number]>();
        for (const row of summaryRows) {
            const key = row.period_start ?? 'all';
            let g = byKey.get(key);
            if (!g) {
                g = {
                    key,
                    label: formatPeriodLabel(granularity, row.period_start),
                    isCurrent: row.is_current,
                    rows: [],
                    total: 0,
                };
                byKey.set(key, g);
                groups.push(g);
            }
            g.rows.push(row);
            g.total += Number(row.total_credit);
        }
        return groups;
    }, [summaryRows, granularity]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gold-fill/20 flex items-center justify-center">
                    <Undo2 className="w-5 h-5 text-gold" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-fg">Returns &amp; Write-offs</h1>
                    <p className="text-sm text-fg-4">Flag returns, match credits, and write off dead stock</p>
                </div>
            </div>

            {/* Summary bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-raised border border-tint/5 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <ArrowDownToLine className="w-4 h-4 text-gold" />
                        <p className="text-xs font-bold text-fg-4 uppercase tracking-wider">Pending Credits</p>
                    </div>
                    <p className="text-2xl font-mono font-bold text-gold">£{pendingTotal.toFixed(2)}</p>
                    <p className="text-xs text-fg-4 mt-1">
                        across {pendingSupplierCount} supplier{pendingSupplierCount !== 1 ? 's' : ''} • {pending.length} return{pending.length !== 1 ? 's' : ''}
                    </p>
                </div>

                <div className="bg-raised border border-tint/5 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-danger" />
                        <p className="text-xs font-bold text-fg-4 uppercase tracking-wider">Written Off This Month</p>
                    </div>
                    <p className="text-2xl font-mono font-bold text-danger">£{writeOffTotalMonth.toFixed(2)}</p>
                    <p className="text-xs text-fg-4 mt-1">
                        <span title={TAX_SAVING_ESTIMATE_TOOLTIP} className="cursor-help border-b border-dotted border-line-glow">
                            est. tax saved ~£{(writeOffTotalMonth * TAX_SAVING_RATE).toFixed(2)}
                        </span>{' '}• {writeOffsThisMonth.length} item{writeOffsThisMonth.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-fd-3xs text-fg-4 mt-0.5">Estimate at basic rate (20% + 9% NIC)</p>
                </div>
            </div>

            {/* Upload zone */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Supplier statement → OCR expense/statement route */}
                <button
                    onClick={() => setUploadOpen(true)}
                    className="w-full flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-gold-fill/10 to-gold-fill/5 border-2 border-dashed border-gold/30 hover:border-gold/50 hover:from-gold-fill/15 hover:to-gold-fill/10 transition-all text-left group"
                >
                    <div className="w-14 h-14 rounded-xl bg-gold-fill/20 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <Camera className="w-7 h-7 text-gold" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <p className="text-base font-bold text-fg">Upload supplier statement</p>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold-fill/20 text-gold text-fd-3xs font-bold">
                                <Sparkles className="w-2.5 h-2.5" /> AUTO-MATCH
                            </span>
                        </div>
                        <p className="text-xs text-fg-3">
                            Snap a photo of your paper statement or upload a PDF. We'll match credits to your returns automatically.
                        </p>
                    </div>
                    <Upload className="w-5 h-5 text-gold/70 group-hover:text-gold transition-colors flex-shrink-0" />
                </button>

                {/* Standalone credit note → isolated credit OCR pipeline */}
                <button
                    onClick={() => setCreditNoteUploadOpen(true)}
                    className="w-full flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-success/10 to-success/5 border-2 border-dashed border-success/30 hover:border-success/50 hover:from-success/15 hover:to-success/10 transition-all text-left group"
                >
                    <div className="w-14 h-14 rounded-xl bg-success/20 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <FileText className="w-7 h-7 text-success" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <p className="text-base font-bold text-fg">Upload credit note</p>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/20 text-success text-fd-3xs font-bold">
                                <Sparkles className="w-2.5 h-2.5" /> AUTO-MATCH
                            </span>
                        </div>
                        <p className="text-xs text-fg-3">
                            Got a single supplier credit note? Upload it here and we'll match it to the return it settles.
                        </p>
                    </div>
                    <Upload className="w-5 h-5 text-success/70 group-hover:text-success transition-colors flex-shrink-0" />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-tint/5">
                <button
                    onClick={() => setActiveTab('pending')}
                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors -mb-px ${activeTab === 'pending'
                        ? 'border-gold text-gold'
                        : 'border-transparent text-fg-4 hover:text-fg-2'
                        }`}
                >
                    Pending Credits {pending.length > 0 && <span className="ml-1 text-xs opacity-70">({pending.length})</span>}
                </button>
                <button
                    onClick={() => setActiveTab('writeoffs')}
                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors -mb-px ${activeTab === 'writeoffs'
                        ? 'border-gold text-gold'
                        : 'border-transparent text-fg-4 hover:text-fg-2'
                        }`}
                >
                    Write-offs {writeOffs.length > 0 && <span className="ml-1 text-xs opacity-70">({writeOffs.length})</span>}
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors -mb-px inline-flex items-center gap-1.5 ${activeTab === 'history'
                        ? 'border-gold text-gold'
                        : 'border-transparent text-fg-4 hover:text-fg-2'
                        }`}
                >
                    <History className="w-3.5 h-3.5" /> Supplier Credits
                </button>
            </div>

            {/* Pending Credits Tab */}
            {activeTab === 'pending' && (
                <>
                    {pending.length === 0 ? (
                        <EmptyState
                            icon={CheckCircle}
                            title="All credits matched"
                            subtitle="No outstanding supplier credits. Flag a part for return from any job to see it here."
                        />
                    ) : (
                        <div className="bg-raised border border-tint/5 rounded-xl overflow-hidden">
                            {/* Desktop table header */}
                            <div className="hidden md:grid grid-cols-[1fr_2fr_110px_130px_130px_155px] gap-4 px-4 py-3 border-b border-tint/5 bg-tint/[0.02] text-fd-3xs uppercase font-bold text-fg-4 tracking-wider">
                                <div>Supplier</div>
                                <div>Part</div>
                                <div>VRM</div>
                                <div>Value</div>
                                <div>Age</div>
                                <div className="text-right">Action</div>
                            </div>
                            <AnimatePresence mode="popLayout">
                                {pending.map((r) => {
                                    const days = daysSince(r.created_at);
                                    const aging = agingColor(days);
                                    const value = r.cost_price * r.quantity;
                                    const isPromptOpen = creditPromptId === r.id;
                                    return (
                                        <motion.div
                                            key={r.id}
                                            layout
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            className="border-b border-tint/5 last:border-b-0"
                                        >
                                            <div className="md:grid md:grid-cols-[1fr_2fr_110px_130px_130px_155px] md:gap-4 md:items-center px-4 py-3 flex flex-col gap-2">
                                                <div className="text-sm font-medium text-fg truncate">{r.supplier}</div>
                                                <div className="text-sm text-fg-2 truncate">{r.part_name}</div>
                                                <div>
                                                    <span className="font-mono text-xs bg-tint/10 px-2 py-1 rounded text-fg whitespace-nowrap">{r.vrm}</span>
                                                </div>
                                                <div className="text-sm font-mono font-bold text-fg whitespace-nowrap">£{value.toFixed(2)}</div>
                                                <div className={`text-xs font-mono inline-flex items-center gap-1 px-2 py-1 rounded border w-fit whitespace-nowrap ${aging.bg} ${aging.text}`}>
                                                    {days}d
                                                    {days >= 21 && <AlertTriangle className="w-3 h-3" />}
                                                </div>
                                                <div className="md:text-right">
                                                    {!isPromptOpen ? (
                                                        <button
                                                            onClick={() => startCreditPrompt(r)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/10 text-success hover:bg-success/20 font-bold text-xs transition-colors min-h-[36px]"
                                                        >
                                                            <CheckCircle className="w-3.5 h-3.5" />
                                                            Mark Credited
                                                        </button>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5 justify-end">
                                                            <span className="text-xs text-fg-3">£</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={creditAmountInput}
                                                                onChange={(e) => setCreditAmountInput(e.target.value)}
                                                                onKeyDown={(e) => e.key === 'Enter' && handleMarkCredited(r.id)}
                                                                autoFocus
                                                                className="w-24 bg-tint/10 border border-success/30 rounded px-2 py-1 text-xs font-mono text-fg focus:outline-none focus:border-success"
                                                            />
                                                            <button
                                                                onClick={() => handleMarkCredited(r.id)}
                                                                disabled={updatingId === r.id}
                                                                className="px-2 py-1 rounded bg-success/20 text-success hover:bg-success/30 transition-colors"
                                                            >
                                                                {updatingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                                                            </button>
                                                            <button
                                                                onClick={() => { setCreditPromptId(null); setCreditAmountInput(''); }}
                                                                className="p-1 text-fg-4 hover:text-fg-2"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {r.status === 'credit_matched' && (
                                                <div className="px-4 pb-3 -mt-1">
                                                    <p className="text-fd-sm-tight text-success flex items-center gap-1">
                                                        <Sparkles className="w-3 h-3" />
                                                        Auto-matched from uploaded statement
                                                    </p>
                                                </div>
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>
                    )}
                </>
            )}

            {/* Write-offs Tab */}
            {activeTab === 'writeoffs' && (
                <>
                    {writeOffs.length === 0 ? (
                        <EmptyState
                            icon={Receipt}
                            title="No write-offs yet"
                            subtitle="Damaged parts written off as business expenses will appear here."
                        />
                    ) : (
                        <div className="bg-raised border border-tint/5 rounded-xl overflow-hidden">
                            <div className="hidden md:grid grid-cols-[125px_1fr_110px_130px_155px] gap-4 px-4 py-3 border-b border-tint/5 bg-tint/[0.02] text-fd-3xs uppercase font-bold text-fg-4 tracking-wider">
                                <div>Date</div>
                                <div>Part</div>
                                <div>VRM</div>
                                <div>Amount</div>
                                <div className="text-right" title={TAX_SAVING_ESTIMATE_TOOLTIP}>Tax Saved (est.)</div>
                            </div>
                            {writeOffs.map((r) => {
                                const value = r.cost_price * r.quantity;
                                const taxSaved = value * TAX_SAVING_RATE;
                                return (
                                    <div
                                        key={r.id}
                                        className="md:grid md:grid-cols-[125px_1fr_110px_130px_155px] md:gap-4 md:items-center px-4 py-3 border-b border-tint/5 last:border-b-0 flex flex-col gap-2"
                                    >
                                        <div className="text-xs text-fg-3 font-mono whitespace-nowrap">
                                            {new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </div>
                                        <div className="text-sm text-fg-2 truncate">
                                            {r.part_name}
                                            {r.notes && <span className="text-fg-4"> • {r.notes}</span>}
                                        </div>
                                        <div>
                                            <span className="font-mono text-xs bg-tint/10 px-2 py-1 rounded text-fg whitespace-nowrap">{r.vrm}</span>
                                        </div>
                                        <div className="text-sm font-mono font-bold text-danger whitespace-nowrap">£{value.toFixed(2)}</div>
                                        <div className="md:text-right text-xs text-fg-4 font-mono whitespace-nowrap" title={TAX_SAVING_ESTIMATE_TOOLTIP}>
                                            <span className="cursor-help">~£{taxSaved.toFixed(2)} est.</span> <CheckCircle className="w-3 h-3 inline text-success ml-1" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {/* Supplier Credits History Tab */}
            {activeTab === 'history' && (
                <div className="space-y-4">
                    {/* Period toggle */}
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="inline-flex rounded-lg bg-raised border border-tint/5 p-1">
                            {(['week', 'month', 'all'] as CreditGranularity[]).map((g) => (
                                <button
                                    key={g}
                                    onClick={() => setGranularity(g)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${granularity === g
                                        ? 'bg-gold-fill/20 text-gold'
                                        : 'text-fg-4 hover:text-fg-2'
                                        }`}
                                >
                                    {g === 'week' ? 'This week' : g === 'month' ? 'By month' : 'All time'}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setAddFormOpen(v => !v)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/10 text-success hover:bg-success/20 font-bold text-xs transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add credit
                        </button>
                    </div>

                    {/* Manual add-credit form (posts to the current period) */}
                    <AnimatePresence>
                        {addFormOpen && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="bg-raised border border-success/20 rounded-xl p-4">
                                    <p className="text-xs text-fg-3 mb-3">
                                        Record a credit you've agreed with a supplier. It posts to this period and updates your P&amp;L and tax pots.
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_1fr_auto] gap-2 items-center">
                                        <input
                                            list="known-suppliers"
                                            value={addSupplier}
                                            onChange={(e) => setAddSupplier(e.target.value)}
                                            placeholder="Supplier"
                                            className="bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-sm text-fg placeholder-fg-4 focus:outline-none focus:border-success/50"
                                        />
                                        <datalist id="known-suppliers">
                                            {knownSuppliers.map(s => <option key={s} value={s} />)}
                                        </datalist>
                                        <div className="flex items-center gap-1">
                                            <span className="text-sm text-fg-4">£</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={addAmount}
                                                onChange={(e) => setAddAmount(e.target.value)}
                                                placeholder="0.00"
                                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-sm font-mono text-fg placeholder-fg-4 focus:outline-none focus:border-success/50"
                                            />
                                        </div>
                                        <input
                                            value={addNote}
                                            onChange={(e) => setAddNote(e.target.value)}
                                            placeholder="Note (optional)"
                                            className="bg-tint/5 border border-tint/10 rounded-lg px-3 py-2 text-sm text-fg placeholder-fg-4 focus:outline-none focus:border-success/50"
                                        />
                                        <button
                                            onClick={handleAddManualCredit}
                                            disabled={addingCredit || !addSupplier.trim() || !(parseFloat(addAmount) > 0)}
                                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-success/20 text-success hover:bg-success/30 disabled:opacity-40 disabled:cursor-not-allowed font-bold text-xs transition-colors min-h-[38px]"
                                        >
                                            {addingCredit ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {summaryLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <Loader2 className="w-6 h-6 animate-spin text-accent" />
                        </div>
                    ) : periodGroups.length === 0 ? (
                        <EmptyState
                            icon={History}
                            title="No supplier credits yet"
                            subtitle="Credits settled with suppliers — auto-matched, marked credited, or added manually — build up here as a running history per supplier."
                        />
                    ) : (
                        periodGroups.map((group) => (
                            <div key={group.key} className="bg-raised border border-tint/5 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-tint/5 bg-tint/[0.02]">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-fg">{group.label}</span>
                                        {group.isCurrent ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/15 text-success text-fd-3xs font-bold">Current</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-tint/5 text-fg-4 text-fd-3xs font-bold">
                                                <Lock className="w-2.5 h-2.5" /> Read-only
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-sm font-mono font-bold text-gold">£{group.total.toFixed(2)}</span>
                                </div>
                                <div>
                                    {group.rows.map((row) => (
                                        <div
                                            key={group.key + row.supplier}
                                            className="flex items-center justify-between px-4 py-2.5 border-b border-tint/5 last:border-b-0"
                                        >
                                            <span className="text-sm text-fg-2">{row.supplier}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-fd-sm-tight text-fg-4">{row.credit_count} credit{row.credit_count !== 1 ? 's' : ''}</span>
                                                <span className="text-sm font-mono font-bold text-fg w-24 text-right">£{Number(row.total_credit).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            <ReceiptUploadModal
                isOpen={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onSuccess={() => fetchReturns()}
                docType="supplier_statement"
                title="Upload supplier statement"
                subtitle="Snap a photo or upload a PDF. We'll extract the credits and match them to your pending returns."
                showJobLink={false}
            />

            <CreditNoteUploadModal
                isOpen={creditNoteUploadOpen}
                onClose={() => setCreditNoteUploadOpen(false)}
                onSuccess={() => fetchReturns()}
            />
        </div>
    );
}

function EmptyState({ icon: Icon, title, subtitle }: { icon: any; title: string; subtitle: string }) {
    return (
        <div className="text-center py-16 bg-raised rounded-xl border border-tint/5">
            <Icon className="w-12 h-12 mx-auto text-fg-4 mb-3" />
            <p className="text-fg-3 font-medium">{title}</p>
            <p className="text-fg-4 text-sm mt-1 max-w-md mx-auto">{subtitle}</p>
        </div>
    );
}
